import { FC } from 'react';
import { Star, MapPin, Image as ImageIcon, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getLocalizedField } from '../../utils/localization';
import { useIsWishlisted, useToggleWishlist } from '../../hooks/useWishlist';

export interface ProductCardProps {
    item: {
        id: string;
        slug?: string;
        name: string;
        price: number;
        compareAtPrice?: number | null;
        image?: string | null;
        images?: { url: string }[] | null;
        rating?: number;
        reviewCount?: number;
        sold?: number;
        location?: string;
        translations?: any[];
        stock?: number;
    };
    className?: string;
}

export const ProductCard: FC<ProductCardProps> = ({ item, className = "" }) => {
    const { t, i18n } = useTranslation('shop');
    const isRTL = i18n.language === 'ar';

    // Resolve image (since different APIs might return `image` string or `images` array)
    const activeImage = item.image || item.images?.[0]?.url || null;

    const localizedName = getLocalizedField(item, 'name', i18n.language);

    // Wishlist logic
    const isWishlisted = useIsWishlisted(item.id);
    const { mutate: toggleWishlist } = useToggleWishlist();

    const handleToggleWishlist = (e: React.MouseEvent) => {
        e.preventDefault(); // Prevent navigating to product page
        e.stopPropagation();

        toggleWishlist(
            { productId: item.id },
            {
                onSuccess: (res) => {
                    if (res.added) {
                        toast.success(t('wishlist.addedToast', 'Added to wishlist'));
                    } else {
                        toast.success(t('wishlist.removedToast', 'Removed from wishlist'));
                    }
                },
                onError: () => toast.error('Failed to update wishlist')
            }
        );
    };

    // Format currency to always be English characters with localized suffix
    const formatCurrency = (amount: number) => {
        const num = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
        return `${num} ${t('common.egp', isRTL ? 'ج.م.' : 'EGP')}`;
    };

    const handleWhatsAppClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const whatsappNumber = "201000000000"; // from seed-homepage.js
        const productUrl = `${window.location.origin}/product/${item.slug || item.id}`;
        const message = `أهلاً، كنت بسأل عن المنتج ده:\n${productUrl}`;
        window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
    };

    return (
        <a href={`/product/${item.slug || item.id}`} dir={isRTL ? 'rtl' : 'ltr'} className={`group bg-white rounded-2xl border border-gray-100 p-3 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col text-start relative ${className}`}>

            {/* Image Area */}
            <div className="relative aspect-square mb-3 flex items-center justify-center bg-gray-50/50 rounded-xl p-4 flex-shrink-0">
                {activeImage ? (
                    <img src={activeImage} alt={localizedName} className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition duration-500" />
                ) : (
                    <ImageIcon size={48} strokeWidth={1} className="text-gray-300" />
                )}

                {/* Wishlist Button Overlay */}
                <button
                    onClick={handleToggleWishlist}
                    className={`absolute top-2 start-2 p-1.5 rounded-full z-10 
                        backdrop-blur-sm shadow-sm transition-all duration-300 transform hover:scale-110 active:scale-95
                        ${isWishlisted
                            ? 'bg-red-50/90 text-red-500 border border-red-100'
                            : 'bg-white/80 text-gray-400 hover:text-red-500 hover:bg-white border border-gray-100'
                        }
                    `}
                    aria-label={isWishlisted ? t('product.removeFromWishlist', 'Remove from Wishlist') : t('product.addToWishlist', 'Add to Wishlist')}
                >
                    <Heart size={16} className={`stroke-[2] transition-colors ${isWishlisted ? 'fill-current' : ''}`} />
                </button>

                {/* Out of Stock or Discount Badge */}
                {item.stock === 0 ? (
                    <div className="absolute top-2 end-2 bg-gray-900 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap z-10">
                        {t('product.outOfStockBadge', 'Out of Stock')}
                    </div>
                ) : item.compareAtPrice && item.compareAtPrice > item.price ? (
                    <div className="absolute top-2 end-2 bg-red-50 text-red-500 text-[10px] font-bold px-2 py-0.5 rounded-full z-10" dir={isRTL ? "rtl" : "ltr"}>
                        {isRTL
                            ? `${Math.round((1 - item.price / item.compareAtPrice) * 100)}% ${t('product.off', 'خصم')}`
                            : `OFF ${Math.round((1 - item.price / item.compareAtPrice) * 100)}%`
                        }
                    </div>
                ) : null}
            </div>

            {/* Content */}
            <div className="flex flex-col gap-1 flex-1">
                <h3 className="font-medium text-sm text-gray-800 line-clamp-2 h-10 leading-snug">{localizedName}</h3>

                <div className="flex-1"></div>

                {/* Price Row */}
                <div className="flex flex-col -gap-0.5 mt-1 items-start">
                    <span className="text-lg font-bold text-gray-900" dir="ltr">{formatCurrency(item.price)}</span>
                    {item.compareAtPrice && item.compareAtPrice > item.price && (
                        <span className="text-[10px] text-gray-400 line-through" dir="ltr">{formatCurrency(item.compareAtPrice)}</span>
                    )}
                </div>

                {/* Rating & Sold */}
                {(Boolean(item.rating && item.rating > 0) || (item.sold != null && item.sold > 0)) && (
                    <div className="flex items-center gap-1.5 mt-1 justify-start">
                        {Boolean(item.rating && item.rating > 0) && (
                            <div className="flex items-center gap-0.5">
                                <Star size={12} className="text-yellow-400 fill-yellow-400" />
                                <span className="text-xs text-gray-600 font-medium" dir="ltr">{item.rating}</span>
                                {Boolean(item.reviewCount && item.reviewCount > 0) && (
                                    <span className="text-xs text-gray-400" dir="ltr">({item.reviewCount})</span>
                                )}
                            </div>
                        )}
                        {Boolean(item.rating && item.rating > 0) && (item.sold != null && item.sold > 0) && (
                            <div className="h-0.5 w-0.5 bg-gray-300 rounded-full"></div>
                        )}
                        {(item.sold != null && item.sold > 0) && (
                            <span className="text-xs text-gray-500">{item.sold}</span>
                        )}
                    </div>
                )}

                {/* Location */}
                {item.location && (
                    <div className="flex items-center gap-1 mt-1 text-gray-400 justify-start">
                        <MapPin size={12} />
                        <span className="text-xs">{item.location}</span>
                    </div>
                )}
            </div>

            {/* WhatsApp Floating Action Button */}
            <button
                onClick={handleWhatsAppClick}
                className="absolute bottom-3 end-3 p-2 bg-secondary hover:bg-secondary/90 text-primary rounded-full shadow-lg transition-transform transform hover:scale-110 active:scale-95 z-20 flex items-center justify-center opacity-80 hover:opacity-100"
                aria-label="Order via WhatsApp"
                title="Order via WhatsApp"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.488-1.761-1.663-2.059-.175-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.82 9.82 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                </svg>
            </button>
        </a>
    );
};
